import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PaymentForm = ({ onNext, onBack, formData, setFormData }) => {
  const [errors, setErrors] = useState({});
  const [selectedPayment, setSelectedPayment] = useState('card');

  const cardTypeOptions = [
    { value: 'visa', label: 'Visa' },
    { value: 'mastercard', label: 'Mastercard' },
    { value: 'amex', label: 'American Express' },
    { value: 'discover', label: 'Discover' }
  ];

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1)?.padStart(2, '0'),
    label: String(i + 1)?.padStart(2, '0')
  }));

  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = new Date()?.getFullYear() + i;
    return { value: String(year), label: String(year) };
  });

  const paymentMethods = [
    { id: 'card', name: 'Credit/Debit Card', icon: 'CreditCard', description: 'Visa, Mastercard, Amex' },
    { id: 'paypal', name: 'PayPal', icon: 'Wallet', description: 'Pay with your PayPal account' },
    { id: 'apple', name: 'Apple Pay', icon: 'Smartphone', description: 'Touch ID or Face ID' },
    { id: 'google', name: 'Google Pay', icon: 'Smartphone', description: 'Pay with Google' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (selectedPayment === 'card') {
      if (!formData?.cardNumber?.trim()) newErrors.cardNumber = 'Card number is required';
      if (!formData?.cardName?.trim()) newErrors.cardName = 'Cardholder name is required';
      if (!formData?.expiryMonth) newErrors.expiryMonth = 'Expiry month is required';
      if (!formData?.expiryYear) newErrors.expiryYear = 'Expiry year is required';
      if (!formData?.cvv?.trim()) newErrors.cvv = 'CVV is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      setFormData(prev => ({ ...prev, paymentMethod: selectedPayment }));
      onNext();
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
          <Icon name="CreditCard" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Payment Method</h2>
          <p className="text-sm text-muted-foreground">Choose your preferred payment option</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Payment Method Selection */}
        <div className="space-y-3">
          {paymentMethods?.map((method) => (
            <div
              key={method?.id}
              className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                selectedPayment === method?.id
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
              onClick={() => setSelectedPayment(method?.id)}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedPayment === method?.id ? 'border-primary' : 'border-border'
                }`}>
                  {selectedPayment === method?.id && (
                    <div className="w-2.5 h-2.5 bg-primary rounded-full" />
                  )}
                </div>
                <Icon name={method?.icon} size={20} className="text-foreground" />
                <div className="flex-1">
                  <div className="font-medium text-foreground">{method?.name}</div>
                  <div className="text-sm text-muted-foreground">{method?.description}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Card Details Form */}
        {selectedPayment === 'card' && (
          <div className="space-y-4 p-4 bg-background/50 rounded-lg border border-border">
            <Input
              label="Card Number"
              type="text"
              placeholder="1234 5678 9012 3456"
              value={formData?.cardNumber || ''}
              onChange={(e) => handleInputChange('cardNumber', e?.target?.value)}
              error={errors?.cardNumber}
              required
            />

            <Input
              label="Cardholder Name"
              type="text"
              placeholder="Enter name on card"
              value={formData?.cardName || ''}
              onChange={(e) => handleInputChange('cardName', e?.target?.value)}
              error={errors?.cardName}
              required
            />

            <div className="grid grid-cols-3 gap-4">
              <Select
                label="Month"
                placeholder="MM"
                options={monthOptions}
                value={formData?.expiryMonth || ''}
                onChange={(value) => handleInputChange('expiryMonth', value)}
                error={errors?.expiryMonth}
                required
              />
              <Select
                label="Year"
                placeholder="YYYY"
                options={yearOptions}
                value={formData?.expiryYear || ''}
                onChange={(value) => handleInputChange('expiryYear', value)}
                error={errors?.expiryYear}
                required
              />
              <Input
                label="CVV"
                type="text"
                placeholder="123"
                value={formData?.cvv || ''}
                onChange={(e) => handleInputChange('cvv', e?.target?.value)}
                error={errors?.cvv}
                required
              />
            </div>
          </div>
        )}

        {/* Express Payment Options */}
        {selectedPayment !== 'card' && (
          <div className="p-6 bg-background/50 rounded-lg border border-border text-center">
            <Icon name={paymentMethods?.find(m => m?.id === selectedPayment)?.icon} size={48} className="mx-auto mb-4 text-primary" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {paymentMethods?.find(m => m?.id === selectedPayment)?.name}
            </h3>
            <p className="text-muted-foreground mb-4">
              You'll be redirected to complete your payment securely
            </p>
          </div>
        )}

        {/* Billing Address Toggle */}
        <div className="pt-4 border-t border-border">
          <Checkbox
            label="Billing address same as shipping address"
            checked={formData?.sameBillingAddress || true}
            onChange={(e) => handleInputChange('sameBillingAddress', e?.target?.checked)}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-6">
          <Button variant="outline" onClick={onBack}>
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back to Shipping
          </Button>
          <Button type="submit" className="px-8">
            Continue to Review
            <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;